import React, { useEffect, useRef, useState } from 'react';
import { openCount, pickerSongs } from './todaySongs';
import './SongPicker.css';

/**
 * The Songs picker (2026-10-02): the whole repertoire behind ONE control at
 * the end of the tab row, so the row holds only what this lesson is using
 * (todaySongs.js). A filter appears once the list is long enough to need it;
 * each song shows its open-note count and whether it is already a pill;
 * Add a song sits at the bottom.
 *
 * The panel renders IN FLOW under the row rather than as an anchored
 * popover: a positioned popover inside the row can be painted under later
 * content or clipped by an ancestor (CLAUDE.md, the TaskQuickBar lesson), and
 * in flow it can neither.
 */
const FILTER_FROM = 7;

const SongPicker = ({ songs = [], today = new Set(), currentSongId = null, onOpen, onAdd }) => {
	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState('');
	const wrap = useRef(null);
	const all = pickerSongs(songs);
	const list = pickerSongs(songs, filter);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') setOpen(false);
		};
		const onDown = (e) => {
			if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onDown);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onDown);
		};
	}, [open]);

	useEffect(() => {
		if (!open) setFilter('');
	}, [open]);

	return (
		<div className="ld-picker" ref={wrap}>
			<button type="button" data-guide="lesson-songs" className={`ld-picker__btn${open ? ' is-open' : ''}`} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
				<i className="fa-sharp fa-solid fa-list-music" aria-hidden="true" />
				Songs
				<span className="ld-picker__total">{all.length}</span>
				<i className={`fa-sharp fa-solid fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
			</button>
			{open && (
				<div className="ld-picker__panel" role="dialog" aria-label="Songs">
					{all.length >= FILTER_FROM && (
						<input
							className="ld-picker__filter"
							type="search"
							placeholder="Filter songs"
							aria-label="Filter songs"
							value={filter}
							autoFocus
							onChange={(e) => setFilter(e.target.value)}
						/>
					)}
					{all.length === 0 ? (
						<p className="ld-picker__empty">No songs yet.</p>
					) : list.length === 0 ? (
						<p className="ld-picker__empty">No song matches "{filter}".</p>
					) : (
						<ul className="ld-picker__list">
							{list.map((s) => {
								const n = openCount(s);
								const on = s.id === currentSongId;
								return (
									<li key={s.id}>
										<button
											type="button"
											className={`ld-picker__row${on ? ' is-current' : ''}`}
											onClick={() => {
												onOpen(s.id);
												setOpen(false);
											}}>
											<i className={`fa-sharp fa-solid ${today.has(s.id) ? 'fa-circle-check' : 'fa-music'}`} aria-hidden="true" />
											<span className="ld-picker__name">{s.name}</span>
											{today.has(s.id) && <span className="ld-picker__tag">this lesson</span>}
											{n > 0 && <span className="ld-picker__count">{n} open</span>}
										</button>
									</li>
								);
							})}
						</ul>
					)}
					<button
						type="button"
						className="ld-picker__add"
						data-guide="lesson-add-song"
						onClick={() => {
							setOpen(false);
							onAdd();
						}}>
						<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
						Add a song
					</button>
				</div>
			)}
		</div>
	);
};

export default SongPicker;
